import OrderPackageStatuses from '@/enums/OrderPackageStatuses';
import { userHasRole, getUserDetails } from '@/util/RoleUtils';
import OrderTypes from '@/enums/OrderTypes';

const {
  DRAFT,
  EPC_PENDING_ASSIGNMENT,
  EPC_REVIEW,
  RETURNED,
  APPROVED,
  AW_PACKAGED,
  COMPLETE,
  HIDDEN,
  SUBMITTED_TO_VENDOR,
  ARCHIVED
} = OrderPackageStatuses;

//ACCESS LOGIC AGREED UPON WITH JON & MEGHAN 9/30/2022

const OrderPackageAccess = {
  isEditModeActive(statusId, isCRSOwner, isEPCOwner, isEditing, isAWPackageEditable) {
    const isEditable = this.isEditable(statusId, isCRSOwner, isEPCOwner, isAWPackageEditable);
    const isEditingOptional = this.isEditingOptional(statusId);

    return isEditable && (isEditing || !isEditingOptional);
  },

  isEditable(statusId, isCRSOwner, isEPCOwner, isAWPackageEditable) {
    /* USE THIS FOR SELECT ORDERS, DELIVERY SCHEDULE, ATTACHMENTS, INFORMATION, NOTES */
    if (EXTERNAL || statusId === ARCHIVED) return false;
    if (userHasRole('Admin')) return true;

    switch (statusId) {
      case DRAFT:
        return isCRSOwner;
      case EPC_REVIEW:
      case RETURNED:
      case APPROVED:
      case COMPLETE:
      case SUBMITTED_TO_VENDOR:
        return isEPCOwner;
      case AW_PACKAGED:
        return isEPCOwner && isAWPackageEditable;
      default:
        return false;
    }
  },

  isSavable(statusId, isCRSOwner, isEPCOwner, isEditing, isAWPackageEditable) {
    /* USE THIS FOR SELECT ORDERS & INFORMATION */
    return this.isEditModeActive(statusId, isCRSOwner, isEPCOwner, isEditing, isAWPackageEditable);
  },

  isSubmittable(statusId, isCRSOwner, isEPCOwner) {
    /* USE THIS FOR SELECT ORDERS, DELIVERY SCHEDULE, ATTACHMENTS, INFORMATION, NOTES */
    if (EXTERNAL) return false;
    switch (statusId) {
      case DRAFT:
      case RETURNED:
        return isCRSOwner || userHasRole('Admin');
      case SUBMITTED_TO_VENDOR:
        return isEPCOwner || userHasRole('Admin');
      default:
        return false;
    }
  },

  isNotesEditable(statusId) {
    if (EXTERNAL || statusId === ARCHIVED) return false;
    return true;
  },

  isDecisionRenderable(statusId, isEPCOwner) {
    if (EXTERNAL) return false;
    switch (statusId) {
      case EPC_REVIEW:
        return isEPCOwner || userHasRole('Admin');
      case RETURNED:
      case APPROVED:
      case AW_PACKAGED:
      case COMPLETE:
      case SUBMITTED_TO_VENDOR:
        return true;
      default:
        return false;
    }
  },

  isDecisionEditable(statusId, isEPCOwner) {
    if (EXTERNAL) return false;
    switch (statusId) {
      case EPC_REVIEW:
        return isEPCOwner || userHasRole('Admin');
      default:
        return false;
    }
  },

  isDecisionSavable(statusId, isEPCOwner) {
    return this.isDecisionEditable(statusId, isEPCOwner);
  },

  isDecisionSubmittable(statusId, isCRSOwner, isEPCOwner) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');
    switch (statusId) {
      case SUBMITTED_TO_VENDOR:
      case EPC_REVIEW:
        return isEPCOwner || isAdmin;
      case RETURNED:
        return (isCRSOwner || isAdmin) && !isEPCOwner;
      default:
        return false;
    }
  },

  isEditViewButtonVisible(statusId, isCRSOwner, isEPCOwner, isAWPackageEditable) {
    const isEditable = this.isEditable(statusId, isCRSOwner, isEPCOwner, isAWPackageEditable);
    const isEditingOptional = this.isEditingOptional(statusId);

    return isEditable && isEditingOptional;
  },

  isEditingOptional(statusId) {
    return [
      EPC_REVIEW,
      RETURNED,
      APPROVED,
      COMPLETE,
      SUBMITTED_TO_VENDOR,
      AW_PACKAGED
    ].includes(statusId);
  },

  isCRSReassignable(statusId, isCRSOwner, isEPCOwner) {
    if (EXTERNAL) return false;

    const isAdmin = userHasRole('Admin');

    switch (statusId) {
      case DRAFT:
        return isCRSOwner || isAdmin;
      case EPC_PENDING_ASSIGNMENT:
        return isAdmin;
      case EPC_REVIEW:
        return isEPCOwner || isAdmin;
      case RETURNED:
        return isCRSOwner || isEPCOwner || isAdmin;
      default:
        return false;
    }
  },

  isEPCReassignable(statusId, isEPCOwner) {
    if (EXTERNAL) return false;

    const isAdmin = userHasRole('Admin');

    switch (statusId) {
      case EPC_PENDING_ASSIGNMENT:
        return isAdmin;
      case EPC_REVIEW:
      case RETURNED:
      case APPROVED:
      case AW_PACKAGED:
      case COMPLETE:
      case SUBMITTED_TO_VENDOR:
        return isEPCOwner || isAdmin;
      default:
        return false;
    }
  },

  isPrintable(orderFormType) {
    if (orderFormType === OrderTypes.RELOCATION) {
      return true;
    }

    return false;
  },

  isDeleteable(statusId, isCRSOwner) {
    if (EXTERNAL) return false;

    const isAdmin = userHasRole('Admin');

    switch (statusId) {
      case DRAFT:
        return isAdmin || isCRSOwner;
      default:
        return false;
    }
  },

  isUnClaimButtonVisible(orderPackage) {
    if (EXTERNAL) return false;

    if (
      OrderPackageStatuses.EPC_REVIEW === orderPackage.statusID &&
      (orderPackage.epcInternalUserID === getUserDetails().id || userHasRole('Admin'))
    ) {
      return true;
    }
    return false;
  },

  isHideButtonVisible(statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');
    if (isAdmin && ![HIDDEN, ARCHIVED].includes(statusId)) {
      return true;
    } else return false;
  },

  isDeliveryScheduleColumnRenderable(renderableBy) {
    if (EXTERNAL) return false;
    if (renderableBy === 'All' || renderableBy === 'Internal') {
      return true;
    } else {
      return false;
    }
  },

  isDeliveryScheduleColumnDownloadable(downloadableBy) {
    if (EXTERNAL) return false;
    if (downloadableBy === 'All' || downloadableBy === 'Internal') {
      return true;
    } else {
      return false;
    }
  }
};

export default OrderPackageAccess;
